import React, {useEffect} from 'react';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrCheckboxList from '../../../../common/arr-checkbox-list/ArrCheckboxList';
import ArrDatePicker from '../../../../common/arr-date-picker/ArrDatePicker';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ExportFormat from './components/ExportFormat';

import {InputAdornment} from '@mui/material';
import {
  ButtonProps,
  CheckboxListProps,
  CheckboxProps,
  ToggleProps,
  DatePickerProps,
  DropdownProps,
  ExportFormatMenuProps,
  MultiSelectDropdownProps,
  RadioGroupProps,
  MultiSelectTreeViewListListProps,
  TextFieldProps,
} from '../../config/reportTypes';

import {ArrSourceSelectedContainerProps} from '../../../tally/selectSourceContainer/ArrSourceSelectedContainer';
import ArrSourceSelectedSection from './components/ArrSourceSelectedSection';
import ArrTreeView from '../../../../common/arr-tree-view/ArrTreeView';
import ArrCheckbox from '../../../../common/arr-checkbox/ArrCheckbox';
import ArrRadio from '../../../../common/arr-radio/ArrRadio';
import ArrMultiSelectDropdown from '../../../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';

const EMPTY_SELECTION: any[] = [];

export interface ArrSourceSelectedSectionWrapperProps extends ArrSourceSelectedContainerProps {
  onChange?: (val: any) => void;
}

export interface ComponentRegistry {
  datePicker: React.FC<DatePickerProps>;
  dropdown: React.FC<DropdownProps>;
  multiselectdropdown: React.FC<MultiSelectDropdownProps>;
  button: React.FC<ButtonProps>;
  checkboxList: React.FC<CheckboxListProps & {value?: any}>;
  arrSourceSelectedSection: React.FC<ArrSourceSelectedSectionWrapperProps>;
  multiSelectTreeViewList: React.FC<MultiSelectTreeViewListListProps>;
  exportFormatMenu: React.FC<ExportFormatMenuProps>;
  textField: React.FC<TextFieldProps>; // ✅ new
  radioGroup: React.FC<RadioGroupProps>;
  checkbox: React.FC<CheckboxProps>;
  toggle: React.FC<ToggleProps>;
}

// --- Implementation that reads extras via a local cast (no signature widening) ---
const datePickerImpl: React.FC<DatePickerProps> = (props) => {
  const {
    label,
    value,
    onChange,
    placeHolder,
    inputClass,
    mode,
    maxDateTime,
    minDateTime,
    triggerOnChangeOnInitialChange = false,
    dataTestId,
  } = props;

  // Read per-instance extras injected from FilterSection safely via a cast
  const extras = props as unknown as {
    initialStartDate?: string;
    initialEndDate?: string;
    dateFormat?: string;
  };

  const forwardedStart = (extras.initialStartDate ?? '').toString().trim();
  const forwardedEnd = (extras.initialEndDate ?? '').toString().trim();
  const forwardedFormat = extras.dateFormat;

  // Existing fallback derivation from `value`
  const derivedStart = Array.isArray(value)
    ? (value?.[0] ?? '')
    : typeof value === 'string'
      ? value
      : '';
  const derivedEnd = Array.isArray(value) ? (value?.[1] ?? '') : '';

  // Prefer explicit extras when present
  const initialStart = (forwardedStart || derivedStart || '').toString().trim();
  const initialEnd = (forwardedEnd || derivedEnd || '').toString().trim();
  const fmt =
    forwardedFormat ??
    (mode === 'date-time' ? 'YYYY/MM/DD HH:mm:ss' : 'YYYY/MM/DD');
  useEffect(() => {
    triggerOnChangeOnInitialChange && onChange?.(initialStart);
  }, [initialStart]);

  return (
    <ArrDatePicker
      label={label}
      selectedDate={value}
      onChange={onChange}
      type="date-picker"
      placeholder={placeHolder}
      inputClass={inputClass}
      initialStartDate={initialStart}
      initialEndDate={initialEnd}
      dateFormat={fmt}
      maxDateTime={maxDateTime}
      minDateTime={minDateTime}
      mode={mode}
      applyEnable={mode === 'date-time'}
      onFocusOpenDatePicker
      dataTestId={dataTestId ?? 'component-registry-button'}
    />
  );
};

export const componentRegistry: ComponentRegistry = {
  datePicker: datePickerImpl,

  dropdown: ({label, size, dropdownItems, value, onChange, searchable}) => (
    <ArrSingleSelectDropdown
      selectedValues={value}
      dropdownLabel={label ? label : ''}
      dropdownItems={dropdownItems ?? []}
      sizeVariant={size}
      onSelectionChange={onChange}
      searchable={searchable}
      dataTestId="asd-dropdownLabel"
    />
  ),
  multiselectdropdown: ({
    label,
    size,
    value,
    onChange,
    options = [],
    searchable,
  }) => (
    <ArrMultiSelectDropdown
      multiple
      dropdownLabel={label ?? ''}
      dropdownItems={options}
      selectedValues={Array.isArray(value) ? value : EMPTY_SELECTION}
      onSelectionChange={(vals) => onChange?.(vals)}
      sizeVariant={size}
      variant="chip"
      searchable={searchable}
      checkboxDataTestId={`component-registry-multiselectdropdown-${String(
        label ?? 'default'
      )
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '-')}-checkbox`}
    />
  ),

  button: ({
    label,
    variant,
    size,
    type,
    className,
    onClick,
    disabled,
    'data-testid': dataTestId,
  }) => (
    <ArrButton
      variant={variant}
      size={size}
      type={type}
      className={className}
      onClick={onClick}
      disabled={disabled}
      dataTestId={dataTestId ?? 'arr-button'}
    >
      {label}
    </ArrButton>
  ),

  checkboxList: ({childLabels, onChange, size, showSelectAll}) => {
    return (
      <ArrCheckboxList
        childLabels={childLabels}
        size={size}
        onChange={onChange}
        showSelectAll={showSelectAll}
      />
    );
  },

  exportFormatMenu: ({label, dropdownItem, selectedValue, onChange}) => (
    <ExportFormat
      label={label}
      dropdownItem={dropdownItem}
      selectedValue={selectedValue}
      onChange={onChange}
    />
  ),

  arrSourceSelectedSection: ({sources, onChange, ...rest}) => {
    const {onSelectionsUpdate: _onSelectionsUpdate, ...restProps} = rest;
    return (
      <ArrSourceSelectedSection
        sources={sources}
        onSelectionsUpdate={onChange || (() => {})}
        {...restProps}
        sortSecurity={true}
      />
    );
  },
  checkbox: ({checked, labelText, size, disabled, onChange, borderRadius}) => (
    <ArrCheckbox
      checked={checked ?? false}
      labelText={labelText}
      size={size || 'medium'}
      disabled={disabled}
      borderRadius={borderRadius}
      onChange={onChange}
      aria-label={`${labelText?.replaceAll(' ', '-')}-cb`}
    />
  ),

  toggle: ({
    value,
    labelText: _labelText,
    toggleSize,
    disabled,
    onChange,
    ariaLabel,
    dataTestId,
  }) => (
    <ArrToggle
      value={value ?? false}
      toggleSize={toggleSize || 'medium'}
      disabled={disabled}
      onChange={(event, checked) => {
        // Extract the checked value from the event or the checked parameter
        const newValue =
          checked !== undefined ? checked : (event?.target?.checked ?? false);
        onChange?.(newValue);
      }}
      ariaLabel={ariaLabel}
      dataTestId={dataTestId ?? 'component-registry-toggle'}
    />
  ),

  radioGroup: ({
    value,
    onChange,
    options,
    name = 'radio-group',
    size = 'xs',
    label,
    disabled,
    className,
    row = true,
    ...rest
  }) => (
    <ArrRadio
      dataTestId={value}
      name={name}
      value={value}
      onChange={onChange}
      data={options}
      size={size}
      label={label}
      disabled={disabled}
      className={className}
      row={row}
      {...rest}
    />
  ),

  multiSelectTreeViewList: ({
    onChange,
    value,
    treeData,
    defaultExpandedIds,
  }) => (
    <ArrTreeView
      itemsList={treeData ?? []}
      selectedItem={''}
      handleNoResultMessage={''}
      allowGlobalSelect={false}
      treeSelectedItems={Array.isArray(value) ? value : EMPTY_SELECTION}
      multipleSelection={true}
      isShowCountLabel={false}
      isShowCheckbox={true}
      showActions={true}
      setSelectedItem={onChange}
      isShowFileType={false}
      isChildrenIndendation={true}
      defaultExpandedIds={defaultExpandedIds}
      isSingleSelectionOnCtrl={true}
      data-testid="multiselect-treeview"
    />
  ),
  textField: (
    {key, label, placeholder, onChange, inputClass, overrideArrProps} // ✅ new
  ) => (
    <ArrTextField
      id={key}
      label={label}
      dataTestId={`comp-reg-${key}`}
      onChange={onChange}
      placeholder={placeholder || 'Enter text here'}
      state="enabled" // 'enabled' | 'error' | 'inactive'
      //supportText=""  // optional helper/error text
      sizeVariant={overrideArrProps?.sizeVariant ?? 'large'} // 'large' | 'medium' | 'small'
      showErrorIcon={true} // show error icon when state="error"
      type={overrideArrProps?.type ?? 'text'} // 'text' | 'password' | 'card'
      className={inputClass || 'search-input'} // custom class if needed
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end" className="search-adornment-icon">
              {/* You can put any custom icon here */}
            </InputAdornment>
          ),
        },
      }}
      {...overrideArrProps}
    />
  ),
};

export default ComponentRegistry;
